import { useEffect, useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "react-bootstrap";
import { FaFilePdf, FaEye, FaEdit, FaTrash } from "react-icons/fa";
import { request } from "../../../utils/api";
import { toast, ToastContainer } from "react-toastify";
import { AbilityContext } from "../../../context/AbilityContext";
import DeleteModal from "../modal/DeleteModal";
import DataTable from "react-data-table-component";
import ViewUserModal from "../modal/ViewUserModal";
import Loading from "../utilComponent/Loading";
import { showImage } from "../../../utils";

const UserList = () => {
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1,
  });

  const navigate = useNavigate();
  const ability = useContext(AbilityContext);

  const userFields = [
    { label: "Avatar", key: "avatar", type: "image" },
    { label: "Name", key: "name" },
    { label: "Email", key: "email" },
    { label: "Phone", key: "phone" },
    { label: "Role", key: "role" },
    { label: "Permissions", key: "permissions" },
    { label: "User Limit", key: "user_limit" },
    { label: "Total Warehouse", key: "wearhouse_limit" },
  ];

  const columns = [
    {
      name: "Avatar",
      cell: (row) => (
        <img
          src={showImage(row.user_image) || "/no-avatar.png"}
          width="40"
          height="40"
          className="rounded-circle"
          alt="avatar"
        />
      ),
      width: "90px",
    },
    {
      name: "Name",
      selector: (row) => row.name || "N/A",
      sortable: true,
    },
    {
      name: "Email",
      selector: (row) => row.email || "N/A",
      sortable: true,
    },
    {
      name: "Phone",
      selector: (row) => row.phone || "N/A",
      sortable: true,
    },
    {
      name: "Role",
      selector: (row) => row.role || "N/A",
      sortable: true,
    },
    {
      name: "User Limit",
      cell: (row) => row.user_limit || "N/A",
    },

    {
      name: "Total Wearhouse",
      cell: (row) => row.wearhouse_limit || "N/A",
    },

    {
      name: "Actions",
      cell: (row) => (
        <div className="d-flex">
          {ability.can("viewUser", "all") && (
            <button
              className="btn btn-sm btn-outline-secondary me-2"
              onClick={() => handleView(row)}
            >
              <FaEye />
            </button>
          )}

          {ability.can("updateUser", "all") && (
            <button
              className="btn btn-sm btn-outline-secondary me-2"
              onClick={() => handleEdit(row)}
            >
              <FaEdit />
            </button>
          )}

          {ability.can("deleteUser", "all") && (
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={() => handleDelete(row)}
            >
              <FaTrash />
            </button>
          )}
        </div>
      ),
      width: "150px",
    },
  ];

  const handleView = (user) => {
    setSelectedUser(user);
    setShowViewModal(true);
  };

  const handleEdit = (user) => {
    navigate(`/user-update/${user.id}`);
  };

  const handleDelete = (user) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const confirmDelete = (id) => {
    setShowDeleteModal(false);
    deleteUser(id)
  };

  const handleSearch = (event) => {
    setSearchText(event.target.value.toLowerCase());
  };

  const filteredUsers = users.filter((item) =>
    Object.values(item).some((val) =>
      String(val).toLowerCase().includes(searchText)
    )
  );

  const deleteUser = async() =>{
    try {
      setLoading(true);
      await request({
        url:`users/${selectedUser?.id}`,
        method:"DELETE"
      })
      toast.success('User deleted Successfully.')
      getTableData();
    } catch (error) {
      toast.error("Cannot delete User")
    }finally{
      setLoading(false)
    }
  }

  const getTableData = async (page = 1) => {
    try {
      setLoading(true);
      const response = await request({
        url: `users?page=${page}&per_page=${pagination.per_page}`,
        method: "GET",
      });

      if (response.status && response.data) {
        setUsers(response.data.items || []);
        setPagination(response.data.pagination || pagination);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getTableData(pagination.current_page);
  }, []);

  const handlePageChange = (page) => {
    getTableData(page);
  };

  // const exportToPDF = () => {};

  return (
    <>
    <ToastContainer/>
    <div>
      <ViewUserModal
        show={showViewModal}
        onClose={() => setShowViewModal(false)}
        data={selectedUser}
        onEdit={handleEdit}
        title={"User Details"}
        fields={userFields}
        updateBtnText={"Update User"}
      />
      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        size="md"
      />

      <div className="page-toolbar px-xl-4 px-sm-2 px-0 py-3">
        <div className="container-fluid">
          <div className="row g-3 mb-3 align-items-center">
            <div className="col">
              <ol className="breadcrumb bg-transparent mb-0">
                <li className="breadcrumb-item">
                  <Link className="text-secondary" to="/">
                    Home
                  </Link>
                </li>
                <li className="breadcrumb-item">
                  <span className="text-secondary">User</span>
                </li>
                <li className="breadcrumb-item active">User List</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      <div className="page-body px-xl-4 px-sm-2 px-0 py-lg-2 py-1 mt-0 mt-lg-3">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className={"card"}>
                <div className="card-header">
                  <h6 className="card-title m-0">User List</h6>
                </div>

                <div className="card-body">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search user..."
                        value={searchText}
                        onChange={handleSearch}
                      />
                    </div>
                    {/* <div className="col-md-6 text-md-end mt-2 mt-md-0">
                      {ability.can("addWarehouse", "all") && (
                        <Button variant="danger" onClick={exportToPDF} disabled={loading}>
                          <FaFilePdf className="me-1" /> Export PDF
                        </Button>
                      )}
                    </div> */}
                  </div>

                  <div className="table-responsive">
                    <DataTable
                      columns={columns}
                      data={filteredUsers}
                      pagination
                      paginationServer
                      paginationTotalRows={pagination.total}
                      onChangePage={handlePageChange}
                      highlightOnHover
                      progressPending={loading}
                      progressComponent={<Loading />}
                      noDataComponent={
                        <div className="text-center py-4">
                          {searchText
                            ? "No matching users found"
                            : "No users available"}
                        </div>
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default UserList;
